import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';

export function Login() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await api.post('/auth/request-link', { email });
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send link');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-ink-50 grid place-items-center px-4">
      <div className="w-full max-w-sm">
        <Link to="/" className="inline-flex items-center gap-2 font-semibold text-lg tracking-tight text-ink-900 mb-8">
          <svg width="22" height="22" viewBox="0 0 32 32" fill="none">
            <rect width="32" height="32" rx="7" fill="#0f172a" />
            <path d="M9 16l5 5 9-11" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          HOA-Vote
        </Link>
        <div className="bg-white border border-ink-200 rounded-xl shadow-card p-6 sm:p-8">
          <h1 className="text-2xl font-semibold tracking-tight">Sign in</h1>
          <p className="mt-1 text-sm text-ink-600">We'll email you a single-use link.</p>

          {sent ? (
            <div className="mt-6 p-4 border border-brand-200 bg-brand-50 rounded-lg text-sm text-brand-900">
              Check your inbox. If an account exists for that email, a sign-in link is on its way.
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <Field label="Email">
                <input
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1.5 w-full border border-ink-200 rounded-lg px-3 py-2.5 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition outline-none"
                />
              </Field>
              {error && <div className="text-sm text-red-600">{error}</div>}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-ink-900 text-white py-2.5 rounded-lg font-medium hover:bg-ink-800 transition disabled:opacity-50"
              >
                {loading ? 'Sending…' : 'Send sign-in link'}
              </button>
              <p className="text-xs text-ink-500 text-center">No password. Link valid 15 minutes.</p>
            </form>
          )}
        </div>
        <p className="text-center text-sm text-ink-500 mt-5">
          New here? <Link to="/signup" className="text-ink-900 font-medium hover:underline">Register your HOA</Link>
        </p>
      </div>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-ink-800">
        {label}
        {hint && <span className="ml-1.5 text-ink-400 font-normal text-xs">({hint})</span>}
      </span>
      {children}
    </label>
  );
}
